import { createSessionKeyedReadCache } from '../../runtime/session-read-cache';
import { loadHeadquartersMeetingSites, loadHeadquartersRecentMeetings } from '../communications/meeting-minutes-repository';
import { loadOperationsSummary, type OperationsSummary } from './operations-summary-port';
import type { HqDashboardResult } from '@kop/contracts';
import { loadSavedHeadquartersDashboard } from './headquarters-saved-read';
import { isTemporaryHqReadFailure } from '../../runtime/hq-read-recovery';

export type HeadquartersMeeting = Readonly<{
  id: string; siteId: string; siteName: string; title: string; date: string;
  status: 'draft' | 'submitted' | 'returned' | 'completed'; files: number; savedAt: string | null;
}>;
export type HeadquartersSite = Readonly<{
  siteId: string; displayName: string; summary: OperationsSummary | null;
  kind?: 'factory' | 'marine' | 'construction-site'; reportingDivisionId?: string;
  summaryState?: HqDashboardResult['sites'][number]['summaryState'];
  inspection?: HqDashboardResult['sites'][number]['inspection'];
  inspectionState?: HqDashboardResult['sites'][number]['inspectionState'];
  halfyearInspection?: HqDashboardResult['sites'][number]['halfyearInspection'];
  halfyearInspectionState?: HqDashboardResult['sites'][number]['halfyearInspectionState'];
  meetings: readonly HeadquartersMeeting[] | null;
}>;

/** Configured sites retain their own saved aggregates; disconnected divisions are never zero. */
export function headquartersAccidentRows(sites: readonly HeadquartersSite[]) {
  const disconnected = ([['construction-site', '시공'], ['marine', '해양']] as const)
    .filter(([kind]) => !sites.some(site => site.kind === kind))
    .map(([kind, displayName]) => ({ siteId: `unconnected-${kind}`, displayName, kind,
      summary: null, meetings: null, disconnected: true as const }));
  return [...sites.map(site => ({ ...site, disconnected: false as const })), ...disconnected];
}
/** Inspection and accident cards expose the same configured business coverage. */
export const headquartersInspectionRows = headquartersAccidentRows;
export type HeadquartersOverview = Readonly<{
  day: string; year: number; sites: readonly HeadquartersSite[];
  half?: 1 | 2; review?: HqDashboardResult['review']; reviewState?: HqDashboardResult['reviewState'];
  historical?: HqDashboardResult['historical']; historicalState?: HqDashboardResult['historicalState'];
}>;

const cache = createSessionKeyedReadCache<{ at: number; value: HeadquartersOverview }>(4);
/** Called only after a verified server save; does not insert client-calculated dashboard values. */
export function invalidateHeadquartersOverview() { cache.invalidate(); }
const selectedHalf = (day: string): 1 | 2 => Number(day.slice(5, 7)) <= 6 ? 1 : 2;
const cacheKey = (uid: string, day: string, year: number, includeSiteMeetings: boolean, half: 1 | 2) => JSON.stringify([uid, day, year, includeSiteMeetings, half]);

export function peekHeadquartersOverview(uid: string, day: string, year: number, includeSiteMeetings = true, half: 1 | 2 = selectedHalf(day)) {
  const result = cache.peek(cacheKey(uid, day, year, includeSiteMeetings, half));
  return result && Date.now() - result.at < 60_000 ? result.value : null;
}

/** Current saved site projections only. Never fall back to a business-ledger scan. */
export async function loadHeadquartersOverview(uid: string, day: string, year: number, fresh = false, includeSiteMeetings = true, half: 1 | 2 = selectedHalf(day)) {
  if (!uid.trim() || !/^\d{4}-\d{2}-\d{2}$/u.test(day) || !Number.isInteger(year) || year < 2000 || year > 2100)
    throw Error('HQ_OVERVIEW_CONTEXT_INVALID');
  const key = cacheKey(uid, day, year, includeSiteMeetings, half);
  const prior = peekHeadquartersOverview(uid, day, year, includeSiteMeetings, half);
  try {
  const result = await cache.read(key, async (): Promise<{ at: number; value: HeadquartersOverview }> => {
    if (!includeSiteMeetings) {
      const saved = await loadSavedHeadquartersDashboard(uid, { day, year, half });
      return { at: Date.now(), value: { ...saved, sites: saved.sites.map(site => ({
        ...site, reportingDivisionId: site.reportingDivisionId ?? undefined, meetings: null,
      })) } };
    }
    const choices = await loadHeadquartersMeetingSites(uid);
    // Bounded batches protect the transport if more divisions/sites are configured.
    const sites: HeadquartersSite[] = [];
    for (let offset = 0; offset < choices.length; offset += 3) {
      const rows = await Promise.all(choices.slice(offset, offset + 3).map(async site => {
        const [summary, meetings] = await Promise.allSettled([
          year === Number(day.slice(0, 4)) ? loadOperationsSummary({
            siteId: site.siteId, day, month: day.slice(0, 7), aggregation: 'daily',
            features: ['inspection-reporting', 'incident'], fresh,
          }) : Promise.resolve(null),
          includeSiteMeetings ? loadHeadquartersRecentMeetings(site.siteId, year) : Promise.resolve(null),
        ]);
        return { ...site,
          summary: summary.status === 'fulfilled' ? summary.value : null,
          meetings: meetings.status === 'fulfilled' && meetings.value ? meetings.value.map(row => ({
            ...row, siteId: site.siteId, siteName: site.displayName,
          })) : null,
        };
      }));
      sites.push(...rows);
    }
    return { at: Date.now(), value: { day, year, sites } };
  }, fresh || prior === null);
  return result.value;
  } catch (error) {
    // A network outage may retain the same session's recent saved read. Revocation,
    // identity changes and invalid evidence must erase it, including failed refreshes.
    if (!isTemporaryHqReadFailure(error)) cache.invalidate(key);
    throw error;
  }
}

/** Totals are withheld when even one configured site's number is unavailable. */
export function headquartersAccidentTotal(sites: readonly HeadquartersSite[], year: number) {
  const values = sites.map(site => site.summary?.accident);
  if (!values.length || values.some(value => !value || value.year !== year)) return null;
  return values.reduce((sum, value) => ({
    industrial: sum.industrial + value!.industrial, minor: sum.minor + value!.minor,
    property: sum.property + value!.property, total: sum.total + value!.total,
    unclassified: sum.unclassified + value!.unclassified,
  }), { industrial: 0, minor: 0, property: 0, total: 0, unclassified: 0 });
}

export function headquartersLatestMeetings(sites: readonly HeadquartersSite[]) {
  return sites.flatMap(site => site.meetings ?? [])
    .sort((a, b) => b.date.localeCompare(a.date) || a.siteName.localeCompare(b.siteName, 'ko-KR') || b.id.localeCompare(a.id))
    .slice(0, 3);
}

/** The newest save must not make an older site's aggregate look up to date. */
export function headquartersProjectionStamp(sites: readonly HeadquartersSite[]) {
  if (!sites.length || sites.some(site => !site.summary)) return null;
  return sites.map(site => site.summary!.generatedAt).sort((a, b) => Date.parse(a) - Date.parse(b))[0]!;
}
